import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Select } from 'antd';
import styled from 'styled-components';
import { CommunicationsPendingHistoryForm, HistoricPendingCommunication, Situation } from 'types';
import { path } from 'ramda';
import moment from 'moment';

import Input from 'components/organisms/input';
import Button from 'components/molecules/button';
import { historicPendingCommunicationActions } from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import { StyledBlueLink } from 'common/styling/mixins';
import FiltersCard from 'components/organisms/filters-card';
import DatePickerForMomentDates from 'components/organisms/datepicker';
import { SearchParams } from 'hooks/use-urlParams';

const { Option } = Select;

interface Props {
  data?: HistoricPendingCommunication;
  senders: string[];
  situations?: Situation[];
  urlSearchParams?: SearchParams;
  onFilterChange: (formValues: CommunicationsPendingHistoryForm) => void;
}

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const Form = (props: Props) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      serviceName: '',
      departureTime: '',
      arrivalTime: '',
      creationStartDate: moment().subtract(10, 'days').format(),
      creationEndDate: moment().format(),
      situationIds: [],
      sender: '',
    },

    onSubmit: (values) => {
      props.onFilterChange({
        ...values,
        departureTime: values.departureTime ? moment(values.departureTime).format('YYYY-MM-DD') : undefined,
        arrivalTime: values.arrivalTime ? moment(values.arrivalTime).format('YYYY-MM-DD') : undefined,
        creationStartDate: values.creationStartDate ? moment(values.creationStartDate).format('YYYY-MM-DD') : undefined,
        creationEndDate: values.creationEndDate ? moment(values.creationEndDate).format('YYYY-MM-DD') : undefined,
        pageNumber: 1,
        maxResultPage: props.data?.maxResultPage,
      });
      dispatch(
        historicPendingCommunicationActions.fetchHistoricPendingCommunication({
          ...values,
          departureTime: values.departureTime ? moment(values.departureTime).format('YYYY-MM-DD') : undefined,
          arrivalTime: values.arrivalTime ? moment(values.arrivalTime).format('YYYY-MM-DD') : undefined,
          creationStartDate: values.creationStartDate
            ? moment(values.creationStartDate).format('YYYY-MM-DD')
            : undefined,
          creationEndDate: values.creationEndDate ? moment(values.creationEndDate).format('YYYY-MM-DD') : undefined,
          pageNumber: 1,
          maxResultPage: props.data?.maxResultPage,
        })
      );
    },
  });

  useEffect(() => {
    const { serviceName = undefined, circulationDate = undefined } = props.urlSearchParams || {};

    if (serviceName) {
      formik.setFieldValue('serviceName', serviceName);
    }
    if (circulationDate) {
      formik.setFieldValue('creationStartDate', undefined);
      formik.setFieldValue('creationEndDate', undefined);
      formik.setFieldValue('arrivalTime', moment(circulationDate).format('YYYY-MM-DD'));
      formik.setFieldValue('departureTime', moment(circulationDate).format('YYYY-MM-DD'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.urlSearchParams]);

  const onReset = () => {
    formik.setFieldValue('serviceName', '');
    formik.setFieldValue('departureTime', '');
    formik.setFieldValue('arrivalTime', '');
    formik.setFieldValue('creationStartDate', moment().subtract(10, 'days').format());
    formik.setFieldValue('creationEndDate', moment().format());
    formik.setFieldValue('sender', '');
    formik.setFieldValue('situationIds', []);
    props.onFilterChange({
      serviceName: '',
      departureTime: '',
      arrivalTime: '',
      creationStartDate: moment().format('YYYY-MM-DD'),
      creationEndDate: moment().format('YYYY-MM-DD'),
      sender: '',
      situationIds: [],
      pageNumber: 1,
      maxResultPage: 20,
    });
    dispatch(
      historicPendingCommunicationActions.fetchHistoricPendingCommunication({
        creationStartDate: moment().subtract(10, 'days').format('YYYY-MM-DD'),
        creationEndDate: moment().format('YYYY-MM-DD'),
        pageNumber: 1,
        maxResultPage: 20,
      })
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <FiltersCard
        title={t('filtre', 'SEARCH_FILTER_TITLE')}
        firstPart={
          <>
            <div>
              <Input
                width="150px"
                upLabel={t('filtre', 'SEARCH_FILTER_TRAIN_NUMBER')}
                name="serviceName"
                onChange={formik.handleChange}
                value={formik.values.serviceName}
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_DEPARTURE_DATE')}</UpLabel>
              <DatePickerForMomentDates
                value={formik.values.departureTime ? moment(formik.values.departureTime) : null}
                format="DD/MM/YYYY"
                style={{ width: '150px', borderRadius: '5px' }}
                onChange={(date) => {
                  formik.setFieldValue('departureTime', date);
                  if (moment(date).isAfter(moment(formik.values.arrivalTime)))
                    formik.setFieldValue('arrivalTime', date);
                }}
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_END_DATE')}</UpLabel>
              <DatePickerForMomentDates
                disabledDate={(current) => {
                  return current && current < moment(formik.values.departureTime);
                }}
                value={formik.values.arrivalTime ? moment(formik.values.arrivalTime) : null}
                style={{ width: '150px', borderRadius: '5px' }}
                format="DD/MM/YYYY"
                onChange={(date) => formik.setFieldValue('arrivalTime', date)}
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_CREATION_DATE_FROM')}</UpLabel>
              <DatePickerForMomentDates
                value={formik.values.creationStartDate ? moment(formik.values.creationStartDate) : null}
                format="DD/MM/YYYY"
                style={{ width: '150px', borderRadius: '5px' }}
                onChange={(date) => {
                  formik.setFieldValue('creationStartDate', date);
                  if (moment(date).isAfter(moment(formik.values.creationEndDate)))
                    formik.setFieldValue('creationEndDate', date);
                }}
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_CREATION_DATE_TO')}</UpLabel>
              <DatePickerForMomentDates
                disabledDate={(current) => {
                  return current && current < moment(formik.values.creationStartDate);
                }}
                value={formik.values.creationEndDate ? moment(formik.values.creationEndDate) : null}
                format="DD/MM/YYYY"
                onChange={(date) => formik.setFieldValue('creationEndDate', date)}
              />
            </div>
            <div>
              <UpLabel>{t('filtre', 'SITUATION')}</UpLabel>
              <Select
                mode="multiple"
                value={formik.values.situationIds}
                dropdownStyle={{ minWidth: '20%' }}
                style={{ minWidth: '250px', borderRadius: '5px', marginLeft: '10px' }}
                popupMatchSelectWidth
                onChange={(value) => formik.setFieldValue('situationIds', value)}
              >
                {props?.situations?.map((x) => (
                  <Option value={x.id} key={x.id}>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore */}
                    {x?.situationCategory?.name} -{x?.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_TRANSMITTER')}</UpLabel>
              <Select
                allowClear
                dropdownStyle={{ minWidth: '20%' }}
                popupMatchSelectWidth
                value={formik.values.sender}
                style={{ minWidth: '150px', borderRadius: '5px' }}
                onChange={(value) => formik.setFieldValue('sender', value)}
              >
                {props.senders?.map((sender, index: number) => (
                  <Option key={index} value={sender}>
                    {sender}
                  </Option>
                ))}
              </Select>
            </div>{' '}
          </>
        }
        secondPart={
          <>
            <Button htmlType="submit" borderRadius="20px" width="130px">
              {t('filtre', 'SEARCH_FILTER_FILTER_BUTTON')}
            </Button>
            <StyledBlueLink onClick={() => onReset()}>{t('filtre', 'SEARCH_FILTER_RESET_BUTTON')}</StyledBlueLink>
          </>
        }
      />
    </form>
  );
};
export default Form;
